import { LitElement, html } from 'lit';

import { getTokensFromUrl, runWebToolFromToolConfig } from '../../modules/WebTools.js';

const TOKENS_THAT_REQUIRE_INPUT = ['node_id', 'section_id', 'connection_id'];

export class MobileWebTool extends LitElement {
  static properties = {
    activeItem: {
      type: String
    },
    activeCommand: {
      type: String
    }
  };

  constructor() {
    super();

    this.activeButtonModel = null;
    this.jobid = null;
  }

  render() {
    return html``;
  }

  willUpdate(changedProperties) {
    // If a changed property is in the list of properties to notify, dispatch a custom event
    // that the property has changed so that a Polymer host element can pick up on the change
    const propertiesToNotify = ['activeCommand', 'activeItem'];
    for (const property of propertiesToNotify) {
      if (changedProperties.has(property)) {
        // Don't notify if the new value is undefined
        if (this[property] == undefined) continue;
        this.dispatchEvent(
          new CustomEvent('lit-property-changed', {
            detail: { property, oldValue: changedProperties.get(property), newValue: this[property] }
          })
        );
      }
    }
  }

  init() {
    if (!this.activeButtonModel?.endpoint_url) {
      this.dispatchEvent(new CustomEvent('toast', { detail: `This tool is not properly configured.` }));
      return;
    }

    this.activeCommand = 'webToolSelect';
    this.dispatchEvent(new CustomEvent('prompt', { detail: { text: `Select a ${this.describeSelectableItems()}` } }));
    this.dispatchEvent(new CustomEvent('cancel-secondary-prompt'));
    this.dispatchEvent(new CustomEvent('cancel-tertiary-prompt'));
  }

  /**
   * This function checks the provided button model and if the endpoint URL only contains job_id tokens or has no tokens,
   * the web tool will run and this function will return true. Otherwise, this function will return false.
   * @param {object} buttonModel the model for the button
   * @param {string} jobId the job id to use for token replacement
   * @returns {boolean} true if the web tool was run, false otherwise
   */
  attemptToRunWebToolImmediately(buttonModel, jobId) {
    if (!buttonModel?.endpoint_url || !jobId) return false;
    const endpointURL = buttonModel?.endpoint_url;
    const tokensFromURL = getTokensFromUrl(endpointURL);

    const urlContainsTokensThatRequireInput = tokensFromURL.some((token) => TOKENS_THAT_REQUIRE_INPUT.includes(token));
    if (urlContainsTokensThatRequireInput) return false;

    runWebToolFromToolConfig(buttonModel, jobId, '');
    return true;
  }

  map_pan(latitude, longitude, closest) {
    if (this.activeCommand != 'webToolSelect') return;

    if (!this.closestItemIsSelectable(closest)) {
      this.activeItem = '';
      return;
    }

    this.activeItem = closest.item;
  }

  /**
   * Creates a string to describe the selectable items for the active button model.
   * @returns {string} A string describing the selectable items for the active button model.
   */
  describeSelectableItems() {
    const selectableItemsTypes = this.getSelectableItemsTypes();
    if (selectableItemsTypes.length < 2) return selectableItemsTypes[0] || 'Unknown';
    const lastItem = selectableItemsTypes.pop();
    return `${selectableItemsTypes.join(', ')}, or ${lastItem}`;
  }

  /**
   * @typedef {'Node'|'Section'|'Connection'} SelectableItemType
   */

  /**
   * Parses the endpoint URL from the active button model to determine the
   * types of selectable items based on the placeholders in the URL.
   * @returns {SelectableItemType[]} An array of selectable item types.
   */
  getSelectableItemsTypes() {
    const endpointURL = this.activeButtonModel?.endpoint_url;
    if (!endpointURL) return [];
    const tokens = getTokensFromUrl(endpointURL);
    /** @type {SelectableItemType[]} */
    const selectableItemsTypes = [];
    if (tokens.includes('node_id')) selectableItemsTypes.push('Node');
    if (tokens.includes('section_id')) selectableItemsTypes.push('Section');
    if (tokens.includes('connection_id')) selectableItemsTypes.push('Connection');
    return selectableItemsTypes;
  }

  /**
   * Returns the selectable type of the closest item.
   * @param {import('./katapult-maps-mobile.js').ClosestItem} closest
   * @returns {SelectableItemType|null} the type of the closest item or null if the closest item is not selectable.
   */
  getClosestItemType(closest) {
    if (!closest?.item) return null;
    switch (closest.item[0]) {
      case 'n':
        return 'Node';
      case 's':
        return 'Section';
      case 'c':
        return 'Connection';
    }
    return null;
  }

  /**
   * Determines if the closest item is selectable
   * @param {import('./katapult-maps-mobile.js').ClosestItem} closest
   * @returns {boolean} True if the closest item is selectable, false otherwise.
   */
  closestItemIsSelectable(closest) {
    const closestItemType = this.getClosestItemType(closest);
    if (!closestItemType) return false;
    return this.getSelectableItemsTypes().includes(closestItemType);
  }

  /**
   * Runs the web tool, replacing any tokens in the endpoint URL with the appropriate values.
   * @param {import('./katapult-maps-mobile.js').ClosestItem} closest
   * @returns {void}
   */
  webToolSelect(closest) {
    if (!this.closestItemIsSelectable(closest)) {
      this.dispatchEvent(new CustomEvent('toast', { detail: `Please select a ${this.describeSelectableItems()}` }));
      return;
    }

    const closestItemID = closest.item.slice(1);

    runWebToolFromToolConfig(this.activeButtonModel, this.jobid, closestItemID);
  }
}
customElements.define('mobile-web-tool', MobileWebTool);
